/**
 * Polls in the conversation (2026-10-24, directions C + D): the article's conversation poll at the
 * top of the panel, the reader's pick as a tag on comments, and the camp chips under the sort row.
 * Only when the article has a conversation poll (`PostContext.conversationPoll`: the body's poll
 * toggled "Use in the conversation", else the polls engine's `post.poll` — `conversationPollMapper`);
 * without one the panel is exactly as before. The poll's state is the one poll store's
 * (`polls/PollStoreContext`), shared with the in-article poll.
 */
import clsx from 'clsx'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import type { CommentHubListPoll, CommentHubPollCamp } from 'shared-code/commenthub-api'
import type { DSPoll, DSPostContext } from 'shared-definitions/types'
import PollFootnote, {
  MineMark,
  removableChoice,
  removeVoteLabel,
  useCanRemoveVote,
} from '../../polls/PollFootnote'
import { PollStoreContext, usePollVote } from '../../polls/PollStoreContext'
import { PollState, pollTotal } from '../../polls/poll-store'
import styles from './ConversationPoll.module.css'

/**
 * The story behind an engine poll, worth a "From" link under the question. WordPress's `from` is
 * an editorial claim (the poll's "Source article" field, else the article that embedded it — WP
 * 713d77aa), not how the poll reached this page: shown for `writer` and `auto` alike, whenever it
 * has a title and a URL and is not this article. Null otherwise — most chip-placed polls have none.
 */
export function pollOrigin(
  poll: Pick<DSPoll, 'from'>,
  postId: number | null | undefined
): NonNullable<DSPoll['from']> | null {
  const { from } = poll
  if (!from || !from.url || !from.title) {
    return null
  }
  return postId && from.id === postId ? null : from
}

/** Characters of a choice label drawn where there is no room for a sentence: the rail card's
 *  buttons and legend, the pick filter's chips and the "Picked" line (2026-10-28, Guillaume —
 *  AA's Pixel poll has 62-character options). The panel's own poll draws the full label. */
export const POLL_LABEL_TRIM = 48

/** The label cut to `max` characters at a word boundary with an ellipsis; untouched when it fits. */
export function trimLabel(label: string, max = POLL_LABEL_TRIM): string {
  if (label.length <= max) {
    return label
  }
  const head = label.slice(0, max - 1)
  const space = head.lastIndexOf(' ')
  return `${(space > max / 2 ? head.slice(0, space) : head).replace(/[\s,;:.!?-]+$/, '')}…`
}

/** "1 vote" / "N votes" — never called with 0 (a poll with no votes shows no count). */
export function votesLabel(n: number): string {
  return `${n} vote${n === 1 ? '' : 's'}`
}

/** The label of a pick, or null when it is not one of the poll's choices (a deleted row). */
export function pickLabel(
  poll: Pick<PollState, 'choices'>,
  pick: string | null | undefined
): string | null {
  return (pick && poll.choices.find(c => c.id === pick)?.label) || null
}

/** The camps worth a chip, in the poll's choice order: known choices with threads (2026-10-28:
 *  no minimum — the first thread shows the row). */
export function visibleCamps(
  poll: Pick<PollState, 'choices'>,
  camps: readonly CommentHubPollCamp[] | null | undefined
): { choiceId: string; label: string; threads: number }[] {
  const threadsById = new Map((camps ?? []).map(c => [c.choiceId, c.threads]))
  const shown = poll.choices
    .map(c => ({ choiceId: c.id, label: c.label, threads: threadsById.get(c.id) ?? 0 }))
    .filter(c => c.threads > 0)
  return shown
}

/** The composer's placeholder once the reader picked (on a later visit: no nudge). */
export function pickedPlaceholder(label: string): string {
  return `You picked ${label}. Say why…`
}

/** The post-vote ask over the composer (panel and rail card), right after a vote. */
export function pickedAsk(label: string): string {
  return `You picked ${label}. What made you pick it?`
}

/** The composer's placeholder under the post-vote ask. */
export const SAY_WHY_PLACEHOLDER = 'Say why…'

/** Each choice's share of the votes, whole percents (0 when nobody voted). */
export function choicePercent(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0
}

/** The reader's pick as a pill after a commenter's name (Have your say's `decorate`). `pop`: the
 *  reader's just-posted comment — the tag pops in. */
export const PickTag: FC<{ label: string; pop?: boolean }> = ({ label, pop }) => (
  <span className={clsx(styles.pickTag, pop && styles.pickTagPop)} title={`Picked ${label}`}>
    {label}
  </span>
)

/**
 * The reader's pick as a chip under a commenter's name (the panel's `decorate`, 2026-10-29,
 * design B): a check and the label on a tint of the accent — the check says "Picked", the word
 * itself is for screen readers. The label is cut at `POLL_LABEL_TRIM`; a cut label is a button —
 * a tap shows the whole sentence (the chip wraps), another cuts it again. `pop`: the just-posted
 * comment's pops in.
 */
const Check: FC<{ className: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

export const PickLine: FC<{ label: string; pop?: boolean }> = ({ label, pop }) => {
  const [open, setOpen] = useState(false)
  const short = trimLabel(label)
  const check = <Check className={styles.pickLineCheck} />
  if (short === label) {
    return (
      <span className={clsx(styles.pickLine, pop && styles.pickLinePop)}>
        {check}
        <span className={styles.pickLineSr}>{'Picked '}</span>
        <span className={styles.pickLineLabel}>{label}</span>
      </span>
    )
  }
  return (
    <button
      type="button"
      className={clsx(
        styles.pickLine,
        styles.pickLineButton,
        open && styles.pickLineOpen,
        pop && styles.pickLinePop
      )}
      aria-expanded={open}
      title={open ? undefined : label}
      onClick={() => setOpen(value => !value)}
    >
      {check}
      <span className={styles.pickLineSr}>{'Picked '}</span>
      <span className={styles.pickLineLabel}>{open ? label : short}</span>
    </button>
  )
}

interface ConversationPollProps {
  poll: DSPoll
  state: PollState
  postMeta: DSPostContext | null
  /** Above 0: a vote just made (the post-vote nudge) — the bars fill from 0, again at each new
   *  key (the bars are re-keyed, never the buttons, which keep focus). 0: drawn still. */
  fillKey?: number
}

/**
 * The poll above the sort row: the question, the choices (bars and percentages once the reader
 * voted, their choice marked), "N votes" (never "0 votes"). Fresh totals from WordPress on open —
 * the page HTML is cached. Every choice stays tappable: the store decides (change, remove — the
 * own pick again, signed in — or "Sign in to change your vote"); `PollFootnote` says which.
 */
const ConversationPoll: FC<ConversationPollProps> = ({ poll, state, postMeta, fillKey = 0 }) => {
  const store = PollStoreContext.useOptionalContainer()
  const vote = usePollVote(poll, 'panel', postMeta)
  const canRemove = useCanRemoveVote()
  useEffect(() => {
    if (!store) {
      return
    }
    // The child's effect runs before the panel's `usePoll` registers it: register first.
    store.register(poll)
    void store.refresh(poll.id)
  }, [store, poll])

  const total = pollTotal(state)
  const voted = state.myChoiceId !== null
  // An engine poll (AA polls engine): the editor's context sentence(s) and the story behind it,
  // each drawn only when set — either alone, both, or neither (a body poll carries neither).
  const context = poll.context?.trim() || null
  const origin = pollOrigin(poll, postMeta?.postId)
  return (
    <section className={clsx(styles.poll, fillKey > 0 && styles.fill)} aria-label="Poll">
      <p className={styles.question}>{state.question}</p>
      {context ? <p className={styles.context}>{context}</p> : null}
      {origin ? (
        <p className={styles.from}>
          {'From '}
          <a href={origin.url}>{origin.title}</a>
        </p>
      ) : null}
      <div className={styles.choices}>
        {state.choices.map(choice => {
          const mine = choice.id === state.myChoiceId
          const percent = choicePercent(choice.value, total)
          return (
            <button
              key={choice.id}
              type="button"
              aria-pressed={mine}
              aria-label={removeVoteLabel(choice.label, mine, canRemove)}
              className={clsx(styles.choice, removableChoice(mine, canRemove), {
                [styles.choiceMine]: mine,
                [styles.choiceBusy]: state.voting,
              })}
              onClick={() => {
                void vote(choice.id)
              }}
            >
              {voted ? (
                // Keyed by the vote (not the button: it keeps focus): the fill plays again.
                <span
                  key={fillKey}
                  className={styles.bar}
                  style={{ transform: `scaleX(${percent / 100})` }}
                  aria-hidden="true"
                />
              ) : null}
              <span className={styles.label}>
                {choice.label}
                {mine ? <MineMark className={styles.mineMark} canRemove={canRemove} /> : null}
              </span>
              {voted ? <span className={styles.percent}>{`${percent}%`}</span> : null}
            </button>
          )
        })}
      </div>
      {total > 0 ? <div className={styles.count}>{votesLabel(total)}</div> : null}
      <PollFootnote poll={poll} state={state} from="panel" postMeta={postMeta} />
    </section>
  )
}

interface ConversationPollCampsProps {
  state: PollState
  /** The last list response's `poll` (from the widget's `belowSortRow`). */
  listPoll: CommentHubListPoll | null
  /** That response's top-level threads (the camp's own under a `choice`). */
  totalThreads: number
  choice: string | null
  onChoice: (choice: string | null) => void
  /** The camps of each unfiltered response, as cached (the panel's ask line reads them). */
  onCamps?: (camps: readonly CommentHubPollCamp[]) => void
}

/** Characters of a camp label on a chip. */
const CHIP_LABEL_TRIM = 22

/**
 * The pick filter under the sort row (the widget's `belowSortRow`, the panel and Have your say,
 * 2026-10-28): a scrolling row of chips — "All · N", the reader's own camp first (a check, "Be
 * the first" while it has no thread), then the other camps with threads, most first. Shown once a
 * camp has a thread or the reader voted. All is the default; a chip narrows the list fetch
 * (`choice`, the host's state). The camps and All's count come from the last unfiltered list — a
 * filtered one keeps the row as it was. A signed-in list's `myChoiceId` seeds the poll store.
 */
export const ConversationPollCamps: FC<ConversationPollCampsProps> = ({
  state,
  listPoll,
  totalThreads,
  choice,
  onChoice,
  onCamps,
}) => {
  const store = PollStoreContext.useOptionalContainer()
  const [cache, setCache] = useState<{
    camps: readonly CommentHubPollCamp[]
    totalThreads: number
  } | null>(null)
  // Read at each response, not on a chip tap: a tap alone must not take the previous
  // (filtered) response's camps and total for All's.
  const choiceRef = useRef(choice)
  choiceRef.current = choice
  const onCampsRef = useRef(onCamps)
  onCampsRef.current = onCamps
  useEffect(() => {
    if (!listPoll) {
      return
    }
    store?.seedMyChoice(state.id, listPoll.myChoiceId)
    if (choiceRef.current === null) {
      setCache({ camps: listPoll.camps, totalThreads })
      onCampsRef.current?.(listPoll.camps)
    }
  }, [listPoll, totalThreads, store, state.id])

  const camps = cache?.camps ?? listPoll?.camps
  const allThreads = cache?.totalThreads ?? totalThreads
  const mine = state.choices.find(c => c.id === state.myChoiceId) ?? null
  const others = visibleCamps(state, camps)
    .filter(c => c.choiceId !== mine?.id)
    // Stable: ties keep the poll's choice order.
    .sort((a, b) => b.threads - a.threads)
  const mineThreads = mine ? camps?.find(c => c.choiceId === mine.id)?.threads ?? 0 : 0
  if (!mine && !others.length) {
    return null
  }
  const chip = (
    key: string,
    value: string | null,
    label: ReactNode,
    count: string,
    extra?: { own: true; title: string }
  ): ReactNode => (
    <button
      key={key}
      type="button"
      aria-pressed={choice === value}
      title={extra?.title}
      className={clsx(styles.campChip, {
        [styles.campChipOwn]: extra?.own,
        [styles.campChipOn]: choice === value,
      })}
      onClick={() => onChoice(value)}
    >
      {label}
      <span className={styles.campChipCount}>{count}</span>
    </button>
  )
  return (
    <div className={styles.campChips} role="group" aria-label="Filter comments by pick">
      {chip('all', null, 'All', String(allThreads))}
      {mine
        ? chip(
            mine.id,
            mine.id,
            <>
              <Check className={styles.campChipCheck} />
              {trimLabel(mine.label, CHIP_LABEL_TRIM)}
            </>,
            mineThreads > 0 ? String(mineThreads) : 'Be the first',
            { own: true, title: 'Your pick' }
          )
        : null}
      {others.map(camp =>
        chip(
          camp.choiceId,
          camp.choiceId,
          trimLabel(camp.label, CHIP_LABEL_TRIM),
          String(camp.threads)
        )
      )}
    </div>
  )
}

export default ConversationPoll
